import { ResponsiveCollection } from '@/interfaces';
import { useGlobalState, useResponsive } from '@/hooks';
import { gtmRemoveParcelConfirm, gtmViewParcel } from '@/utils';
import { removeShipment } from '@/modules/pmt/fetchers';
import { TrackingMilestone } from '@/modules/pmt/interfaces';
import {
  useShipmentDetailsContext,
  useShipmentFiltersContext,
  useShipmentsOverviewContext,
} from '@/modules/pmt/context';
import {
  SORT_OPTIONS,
  HEADER_HEIGHT,
  FILTER_OPTIONS,
  SHIPMENT_FILTER_HEIGHT,
  SHIPMENT_CARD_MARGIN_BOTTOM,
} from '@/modules/pmt/constants';

export const useRemoveShipment = () => {
  const { firestoreUuid } = useGlobalState();
  const { isDesktopPMT } = useResponsive([ResponsiveCollection.DesktopPMT]);
  const { filterConfig } = useShipmentFiltersContext();
  const { allShipments, shipments, deliveredShipments, hasMoreDelivered, setShipments, deletingShipmentsRef } =
    useShipmentsOverviewContext();
  const { setShowDeleteShipment, setShowDetailsView, currentShipment, currentShipmentDocId, setCurrentShipmentDocId } =
    useShipmentDetailsContext();

  const isDeliveredShipment = currentShipment?.data.milestone === TrackingMilestone.Delivered;

  const isInDeliveredSection =
    filterConfig.currentFilter === FILTER_OPTIONS.ALL && filterConfig.currentSort === SORT_OPTIONS.DELIVERY_DATE;

  const jump = isDeliveredShipment && isInDeliveredSection;

  const selectNextShipment = () => {
    if (!allShipments.length) return;

    const deleteShipmentIdx = allShipments.findIndex(
      (shipment) => shipment.data.firestoreDocId === currentShipmentDocId,
    );

    if (deleteShipmentIdx === -1 || deleteShipmentIdx >= allShipments.length) return;

    if (jump) {
      // -1 gets the last item, -2 gets the second last item, which is right above Load More
      // we jump over Load More and select the first item in shipment list
      if (hasMoreDelivered && deleteShipmentIdx === deliveredShipments.length - 2) {
        setCurrentShipmentDocId(shipments[0]?.data.firestoreDocId);
        return;
      }
    }

    const prev = allShipments[deleteShipmentIdx - 1];
    const next = allShipments[deleteShipmentIdx + 1];

    if ((!next && !prev) || (next.isDeleting && prev.isDeleting)) return;

    setCurrentShipmentDocId(next?.data.firestoreDocId || prev?.data.firestoreDocId);

    if (allShipments.length > 1) {
      // Ref: https://www.pivotaltracker.com/n/projects/2537310/stories/183861712
      gtmViewParcel(); // Desktop #3
    }
  };

  const markShipmentDeleteStatus = (isDeleting: boolean) => {
    if (!allShipments.length || !currentShipmentDocId) return;

    const targetShipments = jump ? deliveredShipments : shipments;
    const deleteShipmentIdx = targetShipments.findIndex(
      ({ data: { firestoreDocId } }) => firestoreDocId === currentShipmentDocId,
    );
    if (deleteShipmentIdx === -1) return;

    const targetSet = jump ? 'delivered' : 'overview';
    setShipments((prev) => {
      prev[targetSet][deleteShipmentIdx].isDeleting = isDeleting;
      return { ...prev };
    });
  };

  const onRemoveShipment = async () => {
    if (!firestoreUuid || !currentShipment) return;
    const docId = currentShipment.data.firestoreDocId;

    gtmRemoveParcelConfirm();
    markShipmentDeleteStatus(true);
    deletingShipmentsRef.current.add(docId);

    try {
      await removeShipment({
        uuid: firestoreUuid,
        orderId: currentShipment.data.orderTracking?.orderId,
        shipmentUuid: currentShipment.data.shipmentUuid,
        parcelId: currentShipment.data.parcel?.parcelId,
        carrierId: currentShipment.data.carrierId,
        fbKey: currentShipment.data.firestoreDocId,
      });

      setShowDeleteShipment(false);
      if (isDesktopPMT) {
        selectNextShipment();
      } else {
        setShowDetailsView(false);
        // Hack to scroll to shipment after delete
        setTimeout(() => {
          const element = document.getElementById(docId);
          if (!element) return;
          window.scroll(
            0,
            element.offsetTop - SHIPMENT_CARD_MARGIN_BOTTOM - SHIPMENT_FILTER_HEIGHT - HEADER_HEIGHT - 80,
          );
        });
      }
    } catch (e) {
      console.error(e);
      if (!deletingShipmentsRef.current.has(docId)) return;
      deletingShipmentsRef.current.delete(docId);
      markShipmentDeleteStatus(false);
      setShowDeleteShipment(false);
    }
  };

  return { onRemoveShipment };
};
